import React, { useState, useEffect, useCallback } from 'react';

const sectionStyle = { background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '1.2rem 1.4rem', marginTop: '1.2rem' };

function AccountingReconciliation({ token, selectedProperty }) {
  const [findings, setFindings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lookbackDays, setLookbackDays] = useState(60);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/accounting/cross-module-reconciliation?lookback_days=${lookbackDays}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load reconciliation report');
      setFindings(data.findings || []);
      setSummary(data.summary || null);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [selectedProperty?.id, headers, lookbackDays]);

  useEffect(() => { load(); }, [load]);

  if (!selectedProperty?.id) {
    return (<div style={{ padding: '1rem', maxWidth: 1000 }}><h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Cross-Module Reconciliation</h2><p style={{ color: 'var(--theme-text-muted)' }}>Please select a property.</p></div>);
  }

  const formatCurrency = (v) => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-';
  const sevColors = { CRITICAL: 'var(--theme-danger)', HIGH: '#f97316', MEDIUM: '#f59e0b', LOW: 'var(--theme-text-muted)' };
  const typeLabels = {
    ROOM_CHARGE_DUPLICATE: 'Duplicate Room-Charge Posting',
    ROOM_CHARGE_ORPHAN_POSTING: 'Orphan Room-Charge Posting',
    ROOM_CHARGE_AMOUNT_MISMATCH: 'Room-Charge Amount Mismatch',
    BANQUET_PAYMENT_DRIFT: 'Banquet Payment Drift',
    BANQUET_LEDGER_IMBALANCE: 'Banquet Ledger Imbalance',
  };
  const moduleLabel = (f) => {
    if (f.source_module) return f.source_module === 'KOT' ? 'POS/KOT ↔ PMS Folio' : 'Bar ↔ PMS Folio';
    if (f.anomaly_type?.startsWith('BANQUET')) return 'Banquet ↔ Accounting';
    return '';
  };

  return (
    <div style={{ padding: '1rem', maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h2 style={{ marginTop: 0, color: 'var(--theme-primary)', marginBottom: 4 }}>Cross-Module Reconciliation</h2>
          <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', margin: 0 }}>
            Deterministic checks across the seams where POS/Bar and Banquet money crosses into PMS/Accounting — not an LLM scan, and not a comparison against Tally (this system has no internal chain-wide GL to reconcile against; see the Folio Anomaly Detection screen for the AI-based, folio-only check).
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select value={lookbackDays} onChange={(e) => setLookbackDays(Number(e.target.value))} style={{ padding: '0.4rem 0.6rem', borderRadius: 6, border: '1px solid var(--theme-border)', fontSize: '0.82rem' }}>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
          </select>
          <button onClick={load} disabled={loading} style={{
            padding: '0.5rem 1.2rem', borderRadius: 6, border: 'none', background: loading ? 'var(--theme-text-muted)' : '#ef4444',
            color: '#fff', fontWeight: 600, cursor: loading ? 'wait' : 'pointer', fontSize: '0.85rem',
          }}>{loading ? 'Checking...' : 'Re-check'}</button>
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginTop: '0.5rem' }}>{error}</div>}

      {loading ? (
        <div style={{ color: 'var(--theme-text-muted)', padding: '2rem', textAlign: 'center' }}>Loading...</div>
      ) : findings.length === 0 ? (
        <div style={sectionStyle}><div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--theme-text-muted)' }}>
          <div style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>No reconciliation issues found</div>
          <div style={{ fontSize: '0.85rem' }}>POS/Bar room-charge postings and Banquet payments/ledger all tie out over the selected window.</div>
        </div></div>
      ) : (
        <>
          {summary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem', marginTop: '1rem' }}>
              <SCard label="Total" value={summary.total} color="var(--theme-primary)" />
              <SCard label="Critical" value={summary.critical} color="var(--theme-danger)" />
              <SCard label="High" value={summary.high} color="#f97316" />
              <SCard label="Medium" value={summary.medium} color="#f59e0b" />
              <SCard label="Potential Impact" value={formatCurrency(summary.total_impact)} color="var(--theme-danger)" />
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
            {findings.map((f, idx) => (
              <div key={idx} style={{ background: '#fff', border: `1px solid ${sevColors[f.severity] || 'var(--theme-border)'}`, borderLeft: `4px solid ${sevColors[f.severity]}`, borderRadius: 8, padding: '1rem 1.2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, background: sevColors[f.severity], color: '#fff' }}>{f.severity}</span>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, background: 'var(--theme-border-light)', color: '#475569' }}>{typeLabels[f.anomaly_type] || f.anomaly_type}</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--theme-text-muted)' }}>{moduleLabel(f)}</span>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#334155', marginTop: 4 }}>{f.description}</div>
                    {(f.expected_value || f.actual_value) && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--theme-text-secondary)', marginTop: 4 }}>
                        {f.expected_value && <span>Expected: <strong>{f.expected_value}</strong></span>}
                        {f.expected_value && f.actual_value && <span> | </span>}
                        {f.actual_value && <span>Actual: <strong style={{ color: 'var(--theme-danger)' }}>{f.actual_value}</strong></span>}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    {f.potential_impact > 0 && <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--theme-danger)' }}>{formatCurrency(f.potential_impact)}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SCard({ label, value, color }) {
  return (<div style={{ background: '#fff', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '0.75rem 1rem', textAlign: 'center' }}>
    <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: '1.3rem', fontWeight: 700, color }}>{value}</div>
  </div>);
}

export default AccountingReconciliation;
